import { useEffect, useMemo, useState } from "react";

import styledComponents from "styled-components";

import { ColumnChartGraph, LEGEND_POSITIONS } from "@similarweb/pro-logic-components";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { chosenSites } from "app/common/services/chosenSites";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";

import { EDataType } from "../../../advertisers/overview/components/types";
import { AdIntelMetricsUnavailableBanner } from "../../banners/AdIntelMetricsUnavailableBanner";
import type { AdIntelligenceController } from "../../shared/types/types";
import { getNoDataObject } from "../../utils/utils";
import { usePerformanceQuery } from "../hooks/usePerformanceQuery";

import { createGraphConfig } from "./performanceWidgetCompareGraphConfig";

const WidgetContainerStyled = styledComponents(WidgetContainer)`
    margin-bottom: 24px;
`;

interface IPerformanceWidgetCompareProps {
    controller?: AdIntelligenceController;
}

export const PerformanceWidgetCompare = ({ controller = "advertiser" }: IPerformanceWidgetCompareProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const thirdMetric = controller === "advertiser" ? EDataType.VISITS : EDataType.PAGE_VIEWS;
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const performanceQuery = usePerformanceQuery(controller);

    const performanceData = useMemo(() => performanceQuery.data?.records || {}, [performanceQuery.data?.records]);
    const domains = useMemo(() => params.key.split(","), [params.key]);

    const [selectedDomains, setSelectedDomains] = useState<string[]>([]);

    const totals = useMemo(
        () =>
            Object.values(performanceData).reduce(
                (acc, item) => ({
                    spend: acc.spend + (item.spend?.value || 0),
                    impressions: acc.impressions + (item.impressions?.value || 0),
                    [thirdMetric]: acc[thirdMetric] + (item[thirdMetric]?.value || 0),
                }),
                { spend: 0, impressions: 0, [thirdMetric]: 0 },
            ),
        [performanceData, thirdMetric],
    );

    const dataFormatted = useMemo(
        () =>
            domains.map((domain) => {
                const item = performanceData[domain];
                return {
                    name: domain,
                    color: chosenSites.getSiteColor(domain) as string,
                    data: [
                        totals.spend ? ((item?.spend?.value || 0) / totals.spend) * 100 : 0,
                        totals.impressions ? ((item?.impressions?.value || 0) / totals.impressions) * 100 : 0,
                        totals[thirdMetric] ? ((item?.[thirdMetric]?.value || 0) / totals[thirdMetric]) * 100 : 0,
                    ],
                    visible: selectedDomains.length === 0 ? true : selectedDomains.includes(domain),
                };
            }),
        [domains, performanceData, totals, selectedDomains, thirdMetric],
    );

    const handleLegendSelectionChange = (newlySelectedItems: Array<{ name: string }>) => {
        const domainNames = newlySelectedItems.map((item) => item.name);
        if (domainNames.length === 0) {
            return;
        }
        setSelectedDomains(domainNames);
    };

    useEffect(() => {
        if (domains.length > 0 && selectedDomains.length === 0) {
            setSelectedDomains(domains);
        }
    }, [domains, selectedDomains.length]);

    const graphConfig = useMemo(
        () =>
            createGraphConfig({
                controller,
                thirdMetric,
                totals,
                performanceData,
            }),
        [performanceData, controller, totals, thirdMetric],
    );

    const noDataValues = getNoDataObject(performanceQuery.isError, controller);
    const areAdIntelMetricsUnavailable = performanceQuery.data?.hasAdIntelMetrics === false;

    return (
        <WidgetContainerStyled
            title={translate(`ad.intelligence.${controller}.overview.performance.widget.title`)}
            tooltip={translate(`ad.intelligence.${controller}.overview.performance.widget.title.tooltip`)}
            subtitleFilters={subtitleFilters}
            isLoading={performanceQuery.isPending}
            loaderWidth={"100%"}
            loaderHeight={240}
            height={areAdIntelMetricsUnavailable ? "428px" : "380px"}
            noData={!performanceQuery.isPending ? !performanceQuery.data?.records : null}
            noDataTitle={noDataValues.title}
            noDataSubtitle={noDataValues.subtitle}
        >
            {performanceQuery.data?.hasAdIntelMetrics === false ? <AdIntelMetricsUnavailableBanner /> : null}
            <ColumnChartGraph
                data={dataFormatted}
                graphConfig={graphConfig}
                legendDataAdapter={(series) =>
                    series.map((s) => ({
                        name: s.name,
                        color: s.color as string,
                        id: s.name,
                        visible: s.visible,
                    }))
                }
                legendGraphGap={24}
                isSelectableCheckboxes
                shouldPresentValueAtLegend
                legendPosition={LEGEND_POSITIONS.RIGHT}
                legendToggleHandlerCallback={handleLegendSelectionChange}
            />
        </WidgetContainerStyled>
    );
};
